import { ThemeConfig } from '../types/theme';

/**
 * The design-kit bridge.
 *
 * `MAIE — Design Handoff (CTO)/4 Build sources/_kit/maie.css` is standalone,
 * framework-free CSS with its own 132 class names and 37 custom properties. The
 * design components we port from `deskkit.py` are styled against those property
 * names, not against Tailwind or the app's `--color-*` vars.
 *
 * Rather than fork the kit per tenant, we feed its properties FROM the active
 * `ThemeConfig`. The MAIE default theme carries the design's own values, so a
 * default tenant renders the canvases exactly; a clinic that sets its own
 * primary gets its own brand through the same components, same markup.
 *
 *     tenant ThemeConfig  ->  kit custom properties  ->  design components
 *
 * Scale and elevation are NOT tenant-adjustable. The design fixes them (PLAN §4,
 * a 4-pt grid) and the canvases are measured against them — `lint-desktop.mjs`
 * fails a board whose controls move. Letting a tenant retune spacing would
 * reintroduce exactly the overflow class of bug that linter exists to catch.
 */

/** Kit properties a tenant cannot change — fixed by the design. */
const KIT_CONSTANTS: Record<string, string> = {
  // Radii (PLAN §4)
  '--r-sm': '8px',
  '--r-md': '12px',
  '--r-lg': '16px',
  '--r-pill': '999px',
  // Space — 4-pt grid
  '--m': '20px', // screen margin
  '--pad-card': '16px', // card padding
  '--gap-list': '8px', // tight list gap (12 for roomy)
  '--gap-sec': '24px', // section gap
  // Elevation
  '--shadow-float': '0 2px 8px rgba(22,22,22,.06)',
  '--shadow-overlay': '0 6px 24px rgba(22,22,22,.12)',
  '--scrim': 'rgba(22,22,22,.2)',
  // Pink has no ThemeConfig slot — it is a fixed accent in the kit, used for a
  // single chip variant.
  '--pink-600': '#993556',
  '--pink-100': '#FBEAF2',

  /* BEIGE IS FIXED, NOT THEMED — and that is the whole point of it.
   *
   * Beige is the kit's colour for "true, but not medical": the patient file's
   * quick note ("prefers evening calls · brother of Dr. Waleed"), which must
   * never read as part of the chart. It was derived from the tenant's SECONDARY
   * ramp, so a clinic branded in periwinkle got a periwinkle quick-note band —
   * at which point it reads as a system message rather than a human aside, and
   * the one job the colour has is gone. `--beige-ink` was already hardcoded, so
   * the pair was also internally inconsistent: fixed brown ink on a background
   * that moved with the brand.
   *
   * The dark values are the same hue mixed toward the dark ground rather than
   * the light tints, which would glare — and the INK has to flip with them:
   * #8A6B3F is legible on #FFF4E6 and nearly invisible on #2B241A, so a fixed
   * ink was only ever right for half the schemes. */
};

/** The beige ramp, per scheme. Not derived from the tenant palette — see above. */
const BEIGE = {
  light: { '--beige-500': '#FFDDB7', '--beige-100': '#FFF4E6', '--beige-ink': '#8A6B3F' },
  dark: { '--beige-500': '#6B5836', '--beige-100': '#2B241A', '--beige-ink': '#E8C99A' },
} as const;

/**
 * A soft wash of `color` over the page's own ground.
 *
 * Mixing over `surface` rather than a hardcoded white is what makes these work
 * in dark mode. The kit is light-only — `maie.css` has no dark block — so every
 * wash it publishes sits over white. Reproduce that literally and a dark theme
 * gets light-blue chips on a dark card, which is what the first dark pass
 * showed.
 *
 * In light mode `surface` IS white, so this reproduces the design's published
 * values and the 19/19 parity against maie.css still holds.
 */
function wash(color: string, surface: string, pct = 12): string {
  return `color-mix(in srgb, ${color} ${pct}%, ${surface})`;
}

/**
 * Map a ThemeConfig onto the kit's custom properties.
 *
 * Two mappings are deliberately off-by-one against the obvious reading, because
 * the two scales are anchored differently:
 *   --brand-100  <- primary[50]      the kit's lightest brand step is #EEF0FF,
 *                                    which is the app scale's 50, not its 100.
 *
 * Beige is the exception: it is NOT read off the tenant palette at all — see
 * the BEIGE constant above.
 */
export function kitVarsFor(theme: ThemeConfig): Record<string, string> {
  const { primary, background, text, border } = theme.colors;
  const isDark = theme.scheme === 'dark';
  const ground = background.primary;

  // The brand WASH is the token that cannot simply be read off
  // the scale. A scale runs light -> dark in both schemes, so `primary[50]` is
  // light whether or not the page is. In light we take the design's own value
  // (preserving parity with maie.css); in dark we mix the brand over the dark
  // ground so a "soft" chip is a soft DARK chip.
  const brandWash = isDark ? wash(primary[500], ground, 18) : primary[50];

  return {
    ...KIT_CONSTANTS,

    // Brand core
    '--brand-700': primary[700],
    '--brand-600': primary[600],
    '--brand-500': primary[500],
    '--brand-300': primary[300],
    '--brand-200': primary[200],
    '--brand-100': brandWash,
    ...BEIGE[isDark ? 'dark' : 'light'],

    // Neutrals. surface-0 is the card/sheet ground, surface-50 the app canvas —
    // which is why background.primary maps to 0 and not the other way round.
    '--ink-900': text.primary,
    '--ink-600': text.secondary,
    '--ink-400': text.tertiary,
    '--stroke-200': border.primary,
    '--surface-0': background.primary,
    '--surface-50': background.secondary,
    '--surface-100': background.tertiary,

    // Semantic — functional, not brand
    '--success-600': theme.colors.success,
    '--success-100': wash(theme.colors.success, ground),
    '--warning-600': theme.colors.warning,
    '--warning-100': wash(theme.colors.warning, ground),
    '--danger-600': theme.colors.error,
    '--danger-100': wash(theme.colors.error, ground),
    '--info-600': theme.colors.info,
    '--info-100': wash(theme.colors.info, ground),
  };
}
